import { mensajeSocketSchema, type EventoTorneo } from '@domino/vista'
import { useEffect, useRef } from 'react'
import { leerToken } from './datos/sesion-api.js'

/**
 * Los avisos en vivo de un torneo, por socket.
 *
 * El servidor avisa QUÉ cambió («la mesa X», «la ronda Y») y nunca cómo quedó:
 * quien recibe el aviso vuelve a pedir lo que está mostrando por el camino de
 * siempre. Ver `docs/tiempo-real-pendiente.md` y `apps/api/src/eventos.ts`.
 *
 * Una conexión por torneo y por pestaña del navegador, compartida entre todas
 * las piezas de la pantalla que quieran enterarse: la planilla, la lista de
 * mesas y el torneo escuchan el mismo socket. Se cierra sola un rato después de
 * que se va el último que escuchaba, para que cambiar de pestaña no la corte y
 * la vuelva a abrir.
 *
 * Nada de esto reemplaza al botón «Actualizar» ni al refresco de respaldo: un
 * socket que no conecta (el proxy del club, el wifi de un hotel) no avisa que
 * no conectó, y la pantalla tiene que seguir siendo honesta igual.
 *
 * En la demostración no hay servidor y esto no hace nada.
 */

/** Lo que recibe quien escucha: un aviso, o `listo` al (re)conectar. */
export type Aviso = EventoTorneo | { tipo: 'listo' }

type Oyente = (aviso: Aviso) => void

interface Canal {
  oyentes: Set<Oyente>
  socket: WebSocket | null
  /** Cuánto esperar antes del próximo intento. Crece con cada fallo. */
  espera: number
  reintento: ReturnType<typeof setTimeout> | null
  cierre: ReturnType<typeof setTimeout> | null
  /** Si alguna vez llegó a `listo`: la primera no se avisa, las siguientes sí. */
  conectoAntes: boolean
  /** El servidor dijo que no (4404): no tiene sentido insistir enseguida. */
  rechazado: boolean
}

const ESPERA_INICIAL_MS = 1_000
const ESPERA_MAXIMA_MS = 30_000
/** El servidor cerró con 4404: el torneo no se puede escuchar. Se reintenta mucho después. */
const ESPERA_RECHAZO_MS = 5 * 60_000
/** Cuánto se mantiene abierto el socket sin nadie escuchando. */
const GRACIA_CIERRE_MS = 10_000

const canales = new Map<string, Canal>()

/** La dirección del socket, o null sin API (la demostración). */
function direccion(torneoId: string): string | null {
  const api = import.meta.env.VITE_API_URL
  if (!api) return null
  const url = new URL(api)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/eventos/torneos/${encodeURIComponent(torneoId)}`
  return url.toString()
}

function avisar(canal: Canal, aviso: Aviso): void {
  for (const oyente of canal.oyentes) {
    try {
      oyente(aviso)
    } catch {
      // Una pantalla que falla al recargar no le quita el aviso a las demás.
    }
  }
}

function conectar(clave: string, torneoId: string, publico: boolean, canal: Canal): void {
  const url = direccion(torneoId)
  if (!url || canal.socket || canal.oyentes.size === 0) return
  if (typeof WebSocket === 'undefined') return

  const socket = new WebSocket(url)
  canal.socket = socket

  socket.onopen = () => {
    /* La vitrina saluda sin sesión aunque haya una guardada: es la misma regla
       que sus rutas, y así el permiso del socket coincide con lo que se ve. */
    socket.send(JSON.stringify({ token: publico ? null : leerToken() }))
  }

  socket.onmessage = (mensaje) => {
    let datos: unknown
    try {
      datos = JSON.parse(String(mensaje.data))
    } catch {
      return
    }
    const leido = mensajeSocketSchema.safeParse(datos)
    if (!leido.success) return

    if (leido.data.tipo === 'listo') {
      canal.espera = ESPERA_INICIAL_MS
      canal.rechazado = false
      /* La primera conexión no se avisa: la pantalla acaba de pedir sus datos.
         Una reconexión sí, porque lo que cambió mientras estuvo cortado no se lo
         avisó nadie. */
      if (canal.conectoAntes) avisar(canal, { tipo: 'listo' })
      canal.conectoAntes = true
      return
    }
    avisar(canal, leido.data)
  }

  socket.onclose = (evento) => {
    canal.socket = null
    if (canal.oyentes.size === 0) {
      canales.delete(clave)
      return
    }
    canal.rechazado = evento.code === 4404
    const espera = canal.rechazado ? ESPERA_RECHAZO_MS : canal.espera
    canal.espera = Math.min(canal.espera * 2, ESPERA_MAXIMA_MS)
    canal.reintento = setTimeout(() => {
      canal.reintento = null
      conectar(clave, torneoId, publico, canal)
    }, espera)
  }
}

/**
 * Escucha los avisos de un torneo. Devuelve con qué dejar de escuchar.
 *
 * `publico` es desde la vitrina: saluda sin sesión.
 */
export function escucharTorneo(torneoId: string, publico: boolean, oyente: Oyente): () => void {
  const clave = `${torneoId}|${publico ? 'publico' : 'yo'}`
  let canal = canales.get(clave)
  if (!canal) {
    canal = {
      oyentes: new Set(),
      socket: null,
      espera: ESPERA_INICIAL_MS,
      reintento: null,
      cierre: null,
      conectoAntes: false,
      rechazado: false,
    }
    canales.set(clave, canal)
  }
  const este = canal

  este.oyentes.add(oyente)
  if (este.cierre) {
    clearTimeout(este.cierre)
    este.cierre = null
  }
  conectar(clave, torneoId, publico, este)

  return () => {
    este.oyentes.delete(oyente)
    if (este.oyentes.size > 0) return
    este.cierre = setTimeout(() => {
      este.cierre = null
      if (este.oyentes.size > 0) return
      if (este.reintento) clearTimeout(este.reintento)
      este.reintento = null
      if (este.socket) este.socket.close(1000)
      else canales.delete(clave)
    }, GRACIA_CIERRE_MS)
  }
}

/*
 * Un teléfono que vuelve del bolsillo suele traer el socket muerto: el sistema
 * lo cortó para ahorrar batería y el reintento está esperando su turno. Al
 * volver a la vista se reintenta ya, sin esperar el escalón que tocaba.
 */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    for (const [clave, canal] of canales) {
      if (canal.socket || canal.oyentes.size === 0 || canal.rechazado) continue
      if (canal.reintento) clearTimeout(canal.reintento)
      canal.reintento = null
      canal.espera = ESPERA_INICIAL_MS
      const [torneoId, desde] = clave.split('|')
      if (torneoId) conectar(clave, torneoId, desde === 'publico', canal)
    }
  })
}

/**
 * Escucha los avisos de un torneo mientras el componente está montado.
 *
 * `alAvisar` puede cambiar en cada render sin reabrir nada: se guarda en una
 * referencia y el socket se abre una sola vez por torneo.
 */
export function useAvisosDelTorneo(
  torneoId: string | null,
  publico: boolean,
  alAvisar: (aviso: Aviso) => void,
): void {
  const ultimo = useRef(alAvisar)
  ultimo.current = alAvisar

  useEffect(() => {
    if (!torneoId) return
    return escucharTorneo(torneoId, publico, (aviso) => ultimo.current(aviso))
  }, [torneoId, publico])
}

/**
 * Lo que se espera, como mínimo, entre un aviso y el pedido que provoca, en las
 * pantallas de la vitrina.
 *
 * Las respuestas públicas se guardan dos segundos en el borde de Cloudflare
 * (ver `apps/api/src/rutas-publicas.ts`): un pedido hecho apenas llega el aviso
 * puede recibir la copia de antes de la mano y quedarse con ella. Pasado este
 * rato, cualquier copia que se sirva ya es posterior al aviso. Con sesión no
 * hay caché y no hace falta esperar.
 */
export const ESPERA_CACHE_PUBLICA_MS = 2_500

/**
 * Una llamada que se hace como mucho una vez cada `intervaloMs`.
 *
 * Es el freno de las listas grandes: en una ronda de cien mesas se anota una
 * mano cada pocos segundos, y si cada aviso recargara la ronda entera en cada
 * teléfono que la mira, el socket costaría más que el refresco que vino a
 * reemplazar. Con el freno, quien mira la lista la ve al día cada tanto, y los
 * avisos que llegan en el medio se juntan en una sola recarga al final.
 *
 * El primer aviso espera un poco al azar antes de disparar: cuatrocientos
 * teléfonos que reciben el mismo aviso en el mismo milisegundo no tienen por
 * qué pedir todos en ese mismo milisegundo.
 *
 * `minimoMs` es lo que se espera siempre después de un aviso, aunque el freno
 * ya lo permita (en la vitrina, `ESPERA_CACHE_PUBLICA_MS`). Un aviso que llega
 * cuando el pedido está por salir no alcanza a cumplirlo: ese pedido sale
 * igual, y queda otro programado para después.
 */
export function useLlamadaEspaciada(
  llamar: () => void,
  intervaloMs: number,
  minimoMs = 0,
): () => void {
  const ultimo = useRef(llamar)
  ultimo.current = llamar
  const estado = useRef<{
    ultimaVez: number
    ultimoAviso: number
    pendiente: ReturnType<typeof setTimeout> | null
  }>({ ultimaVez: 0, ultimoAviso: 0, pendiente: null })

  useEffect(
    () => () => {
      if (estado.current.pendiente) clearTimeout(estado.current.pendiente)
    },
    [],
  )

  const pedido = useRef(() => {
    const e = estado.current
    e.ultimoAviso = Date.now()
    if (e.pendiente) return
    const programar = () => {
      const falta = Math.max(minimoMs, e.ultimaVez + intervaloMs - Date.now())
      const azar = Math.random() * 2_000
      e.pendiente = setTimeout(() => {
        e.pendiente = null
        e.ultimaVez = Date.now()
        ultimo.current()
        if (e.ultimaVez - e.ultimoAviso < minimoMs) programar()
      }, falta + azar)
    }
    programar()
  })
  return pedido.current
}
